import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDomainService } from '@asd-stan/domain/infrastructure/getters';
import { useOnClickOutside } from '@asd-stan/helpers/use-in-click-outside';
import { useRequest } from '@asd-stan/helpers/use-request';
import { UserStageNotify } from '@asd-stan/user/api/user.repo';
import { SystemRole } from '@asd-stan/user/domain/system-role.entity';
import { UserSelectValue } from '@asd-stan/user/domain/user-select-value.entity';
import { User } from '@asd-stan/user/domain/user.entity';
import { getUserService } from '@asd-stan/user/infrastructure/getters';
import { WorkingGroup } from '@asd-stan/working-group/domain/working-group.entity';
import { Avatar } from '@components/avatar/avatar';
import { Checkbox } from '@components/checkbox/checkbox';
import { Loading } from '@components/loading/loading';
import { Flex } from '@components/utility/flex';
import { StyledFieldErrorMessage } from '@components/utility/field-error-message.styled';

import { StyledUserSelect } from './additional.styled';
import { ReactComponent as ArrowIcon } from './assets/arrow.svg';
import { ReactComponent as CloseIcon } from './assets/close.svg';
import { generatePlaceholder } from 'src/helpers/app-utils';


const coreNotificationRoles = [
	SystemRole.DIRECTOR,
	SystemRole.ES,
	SystemRole.BOARD_MEMBER,
	SystemRole.TAC,
	SystemRole.MC,
];

const UserRow = ({
	user,
	selectedUsers,
	onUserSelectedChange: handleUserSelectedChange,
}: {
	user: User;
	selectedUsers: UserSelectValue[];
	onUserSelectedChange(user: User): void;
}) => {
	const { id, firstName, lastName } = user;

	return (
		<div className="user" key={id} onClick={() => handleUserSelectedChange(user)}>
			<div className="user-left">
				<Checkbox
					checked={selectedUsers.some(selectedUser => selectedUser.id === id)}
					onChange={() => handleUserSelectedChange(user)}
				/>
				<div className="avatar">
					<Avatar width="24px" height="24px" />
				</div>
				<div className="name">
					{firstName} {lastName}
				</div>
			</div>
			<div className="roles">
				{(user.systemRoles ?? []).map(role => (
					<div className="role" key={role}>
						{role}
					</div>
				))}
			</div>
		</div>
	);
};

const WorkingGroupBlock = ({
	workingGroup,
	selectedUsers,
	onUserSelectedChange: handleUserSelectedChange,
	onAllUsersSelectedChange: handleAllUsersSelectedChange,
	stageNotifyUsers,
	isStageNotifyLoading,
	onNeedStageNotifyLoad,
}: {
	workingGroup: WorkingGroup;
	selectedUsers: UserSelectValue[];
	onUserSelectedChange(user: User): void;
	onAllUsersSelectedChange(users: User[], selected: boolean): void;
	stageNotifyUsers: UserStageNotify[] | null;
	isStageNotifyLoading: boolean;
	onNeedStageNotifyLoad(): void;
}) => {
	const { t } = useTranslation();
	const [opened, setOpened] = useState(false);
	const [pendingSelectAll, setPendingSelectAll] = useState<boolean | null>(null);

	const participants = useMemo(() => {
		if (!stageNotifyUsers) {
			return null;
		}

		const participantsMap = new Map<number, User>();
		stageNotifyUsers.forEach(stageNotifyUser => {
			if (stageNotifyUser.workingGroupCodes.includes(workingGroup.code)) {
				participantsMap.set(stageNotifyUser.user.id, stageNotifyUser.user);
			}
		});

		return Array.from(participantsMap.values());
	}, [stageNotifyUsers, workingGroup.code]);

	const allUsersSelected =
		!!participants &&
		participants.length > 0 &&
		participants.every(({ id }) => selectedUsers.some(selectedUser => selectedUser.id === id));

	useEffect(() => {
		if (pendingSelectAll === null || !participants) {
			return;
		}

		handleAllUsersSelectedChange(participants, pendingSelectAll);
		setPendingSelectAll(null);
	}, [pendingSelectAll, participants, handleAllUsersSelectedChange]);

	return (
		<div className="working-group">
			<div
				className="working-group-title"
				onClick={() => {
					const nextOpened = !opened;
					setOpened(nextOpened);
					if (nextOpened) {
						onNeedStageNotifyLoad();
					}
				}}>
				<Checkbox
					checked={allUsersSelected}
					onChange={() => {
						const nextSelected = !allUsersSelected;

						if (!participants) {
							setOpened(true);
							onNeedStageNotifyLoad();
							setPendingSelectAll(nextSelected);
							return;
						}

						if (nextSelected) {
							setOpened(true);
						}
						handleAllUsersSelectedChange(participants, nextSelected);
					}}
				/>
				{workingGroup.code} {workingGroup.name}
				<ArrowIcon className={opened ? 'opened' : undefined} />
			</div>
			{opened &&
				(isStageNotifyLoading && !participants ? (
					<Flex $justify="center" className="loading">
						<Loading width={25} height={25} />
					</Flex>
				) : !!participants && participants.length ? (
					participants.map(user => (
						<UserRow
							key={user.id}
							user={user}
							selectedUsers={selectedUsers}
							onUserSelectedChange={handleUserSelectedChange}
						/>
					))
				) : (
					<div className="empty">
						{t('standard.singleStandard.stageUpdateModal.additional.noUsers')}
					</div>
				))}
		</div>
	);
};

const Domain = ({
	code,
	name,
	workingGroups,
	selectedUsers,
	onUserSelectedChange: handleUserSelectedChange,
	onAllUsersSelectedChange: handleAllUsersSelectedChange,
}: {
	code: string;
	name: string;
	workingGroups: WorkingGroup[];
	selectedUsers: UserSelectValue[];
	onUserSelectedChange(user: User): void;
	onAllUsersSelectedChange(users: User[], selected: boolean): void;
}) => {
	const { t } = useTranslation();
	const userService = getUserService();
	const [opened, setOpened] = useState(false);
	const [shouldLoadParticipants, setShouldLoadParticipants] = useState(false);
	const [shouldLoadStageNotifyUsers, setShouldLoadStageNotifyUsers] = useState(false);
	const [pendingSelectAll, setPendingSelectAll] = useState<boolean | null>(null);

	const { data: participants, isLoading: isParticipantsLoading } = useRequest(
		useCallback(() => userService.getDomainParticipants([code]), [code]),
		{
			disabled: !shouldLoadParticipants,
		}
	);

	const { data: stageNotifyUsers, isLoading: isStageNotifyLoading } = useRequest(
		useCallback(() => userService.getUserStageNotifyList(), []),
		{
			disabled: !shouldLoadStageNotifyUsers,
		}
	);

	const domainUserIds = useMemo(() => {
		if (!participants) {
			return new Set<number>();
		}
		return new Set(participants.map(user => user.id));
	}, [participants]);

	const groupedUserIds = useMemo(() => {
		if (!participants || !stageNotifyUsers) {
			return new Set<number>();
		}

		const workingGroupCodes = new Set(workingGroups.map(workingGroup => workingGroup.code));
		const userIds = new Set<number>();

		stageNotifyUsers.forEach(stageNotifyUser => {
			if (!domainUserIds.has(stageNotifyUser.user.id)) {
				return;
			}

			if (stageNotifyUser.workingGroupCodes.some(groupCode => workingGroupCodes.has(groupCode))) {
				userIds.add(stageNotifyUser.user.id);
			}
		});

		return userIds;
	}, [participants, stageNotifyUsers, workingGroups, domainUserIds]);

	const otherUsers = useMemo(() => {
		if (!participants) {
			return [];
		}

		if (!stageNotifyUsers || workingGroups.length === 0) {
			return participants;
		}

		return participants.filter(user => !groupedUserIds.has(user.id));
	}, [participants, stageNotifyUsers, groupedUserIds, workingGroups.length]);

	const allUsersSelected =
		!!participants &&
		participants.length > 0 &&
		participants.every(({ id }) => selectedUsers.some(selectedUser => selectedUser.id === id));

	useEffect(() => {
		if (pendingSelectAll === null || !participants) {
			return;
		}

		handleAllUsersSelectedChange(participants, pendingSelectAll);
		setPendingSelectAll(null);
	}, [pendingSelectAll, participants, handleAllUsersSelectedChange]);

	return (
		<div className="domain">
			<div
				className="domain-title"
				onClick={() => {
					const nextOpened = !opened;
					setOpened(nextOpened);
					if (nextOpened) {
						setShouldLoadParticipants(true);
					}
				}}>
				<Checkbox
					checked={allUsersSelected}
					onChange={() => {
						const nextSelected = !allUsersSelected;

						if (!participants) {
							setOpened(true);
							setShouldLoadParticipants(true);
							setPendingSelectAll(nextSelected);
							return;
						}

						if (nextSelected) {
							setOpened(true);
						}
						handleAllUsersSelectedChange(participants, nextSelected);
					}}
				/>
				{code} {name}
				<ArrowIcon className={opened ? 'opened' : undefined} />
			</div>
			{opened &&
				(isParticipantsLoading ? (
					<Flex $justify="center" className="loading">
						<Loading width={25} height={25} />
					</Flex>
				) : !!participants && participants.length ? (
					<>
						{workingGroups.map(workingGroup => (
							<WorkingGroupBlock
								key={workingGroup.code}
								workingGroup={workingGroup}
								selectedUsers={selectedUsers}
								onUserSelectedChange={handleUserSelectedChange}
								onAllUsersSelectedChange={handleAllUsersSelectedChange}
								stageNotifyUsers={stageNotifyUsers}
								isStageNotifyLoading={isStageNotifyLoading}
								onNeedStageNotifyLoad={() => setShouldLoadStageNotifyUsers(true)}
							/>
						))}
						{otherUsers.map(user => (
							<UserRow
								key={user.id}
								user={user}
								selectedUsers={selectedUsers}
								onUserSelectedChange={handleUserSelectedChange}
							/>
						))}
						{workingGroups.length > 0 && isStageNotifyLoading && !stageNotifyUsers && (
							<Flex $justify="center" className="loading">
								<Loading width={25} height={25} />
							</Flex>
						)}
					</>
				) : (
					<div className="empty">
						{t('standard.singleStandard.stageUpdateModal.additional.noUsers')}
					</div>
				))}
		</div>
	);
};

const OtherUsers = ({
	selectedUsers,
	onUserSelectedChange: handleUserSelectedChange,
	onAllUsersSelectedChange: handleAllUsersSelectedChange,
}: {
	selectedUsers: UserSelectValue[];
	onUserSelectedChange(user: User): void;
	onAllUsersSelectedChange(users: User[], selected: boolean): void;
}) => {
	const { t } = useTranslation();
	const userService = getUserService();
	const [opened, setOpened] = useState(false);
	const hasBeenOpenedRef = useRef(false);

	if (!hasBeenOpenedRef.current && opened) {
		hasBeenOpenedRef.current = true;
	}

	const { data: participants, isLoading: isParticipantsLoading } = useRequest(
		useCallback(
			() =>
				userService.getUserList({
					isPagination: false,
					systemRoles: coreNotificationRoles,
				}),
			[]
		),
		{
			disabled: !hasBeenOpenedRef.current,
		}
	);

	const allUsersSelected =
		!!participants &&
		participants.data.length > 0 &&
		participants.data.every(({ id }) => selectedUsers.some(selectedUser => selectedUser.id === id));

	return (
		<div className="other-user">
			<div className="other-user-title" onClick={() => setOpened(value => !value)}>
				<Checkbox
					checked={allUsersSelected}
					onChange={() => {
						if (!participants) {
							setOpened(value => !value);
							return;
						}
						if (!allUsersSelected) {
							setOpened(true);
						}
						handleAllUsersSelectedChange(participants.data, !allUsersSelected);
					}}
				/>
				{t('standard.singleStandard.stageUpdateModal.additional.otherUsers')}
				<ArrowIcon className={opened ? 'opened' : undefined} />
			</div>
			{opened &&
				(isParticipantsLoading ? (
					<Flex $justify="center" className="loading">
						<Loading width={25} height={25} />
					</Flex>
				) : !!participants && participants.data.length ? (
					participants.data.map(user => (
						<UserRow
							key={user.id}
							user={user}
							selectedUsers={selectedUsers}
							onUserSelectedChange={handleUserSelectedChange}
						/>
					))
				) : (
					<div className="empty">
						{t('standard.singleStandard.stageUpdateModal.additional.noUsers')}
					</div>
				))}
		</div>
	);
};

const NotAllocatedUsers = ({
	selectedUsers,
	onUserSelectedChange: handleUserSelectedChange,
	onAllUsersSelectedChange: handleAllUsersSelectedChange,
}: {
	selectedUsers: UserSelectValue[];
	onUserSelectedChange(user: User): void;
	onAllUsersSelectedChange(users: User[], selected: boolean): void;
}) => {
	const { t } = useTranslation();
	const userService = getUserService();
	const [opened, setOpened] = useState(false);
	const hasBeenOpenedRef = useRef(false);

	if (!hasBeenOpenedRef.current && opened) {
		hasBeenOpenedRef.current = true;
	}

	const { data: participants, isLoading: isParticipantsLoading } = useRequest(
		useCallback(
			() =>
				userService.getUserList({
					isPagination: false,
					excludeParticipation: true,
					systemRoles: coreNotificationRoles,
				}),
			[]
		),
		{
			disabled: !hasBeenOpenedRef.current,
		}
	);

	const allUsersSelected =
		!!participants &&
		participants.data.length > 0 &&
		participants.data.every(({ id }) => selectedUsers.some(selectedUser => selectedUser.id === id));

	return (
		<div className="not-allocated">
			<div className="not-allocated-title" onClick={() => setOpened(value => !value)}>
				<Checkbox
					checked={allUsersSelected}
					onChange={() => {
						if (!participants) {
							setOpened(value => !value);
							return;
						}
						if (!allUsersSelected) {
							setOpened(true);
						}
						handleAllUsersSelectedChange(participants.data, !allUsersSelected);
					}}
				/>
				{t('standard.singleStandard.stageUpdateModal.additional.notAllocated')}
				<ArrowIcon className={opened ? 'opened' : undefined} />
			</div>
			{opened &&
				(isParticipantsLoading ? (
					<Flex $justify="center" className="loading">
						<Loading width={25} height={25} />
					</Flex>
				) : !!participants && participants.data.length ? (
					participants.data.map(user => (
						<UserRow
							key={user.id}
							user={user}
							selectedUsers={selectedUsers}
							onUserSelectedChange={handleUserSelectedChange}
						/>
					))
				) : (
					<div className="empty">
						{t('standard.singleStandard.stageUpdateModal.additional.noUsers')}
					</div>
				))}
		</div>
	);
};

type Props = {
	fieldTitle: string;
	value: UserSelectValue[];
	onChange(value: UserSelectValue[]): void;
	disabled?: boolean;
	error?: string;
	onBlur?(): void;
};

export const UserSelectSearch = ({
	fieldTitle,
	value,
	onChange: handleChange,
	disabled = false,
	error,
	onBlur,
}: Props) => {
	const { t } = useTranslation();
	const domainService = getDomainService();
	const userService = getUserService();
	const [opened, setOpened] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
	const userSelectRef = useRef<HTMLDivElement>(null);

	const { data: domains, isLoading: isDomainsLoading } = useRequest(
		useCallback(() => domainService.getDomains(), [])
	);

	const selectedUsers = value;
	const setSelectedUsers = (newValue: UserSelectValue[]) => handleChange(newValue);

	const handleUserSelectedChange = (user: User) => {
		if (searchQuery) {
			setSearchQuery('');
		}
		if (selectedUsers.some(({ id }) => id === user.id)) {
			setSelectedUsers(selectedUsers.filter(({ id }) => id !== user.id));
			return;
		}
		setSelectedUsers([...selectedUsers, user]);
	};

	const handleAllUsersInDomainSelectedChange = (usersInDomain: User[], selected: boolean) => {
		if (selected) {
			const nextSelectedUsers = selectedUsers.slice();
			usersInDomain.forEach(user => {
				if (nextSelectedUsers.some(({ id }) => id === user.id)) {
					return;
				}
				nextSelectedUsers.push(user);
			});
			setSelectedUsers(nextSelectedUsers);
			return;
		}
		setSelectedUsers(selectedUsers.filter(({ id }) => !usersInDomain.some(user => user.id === id)));
	};

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery.trim());
		}, 700);

		return () => clearTimeout(timeoutId);
	}, [searchQuery]);

	const isSearchActive = debouncedSearchQuery.length > 0;

	const { data: searchedUsers, isLoading: isSearchUsersLoading } = useRequest(
		useCallback(
			() =>
				userService.getUserList({
					isPagination: false,
					withDeleted: false,
					search: debouncedSearchQuery,
				}),
			[debouncedSearchQuery]
		),
		{
			disabled: !opened || !isSearchActive,
		}
	);

	useOnClickOutside(userSelectRef, () => {
		setOpened(false);
		setSearchQuery('');
		onBlur?.();
	});

	return (
		<StyledUserSelect $disabled={disabled} $error={!!error}>
			<div className="label">
				{fieldTitle}
			</div>
			<div ref={userSelectRef}>
				<div
					className="input"
					onClick={() => {
						if (!disabled) {
							setOpened(true);
						}
					}}>
					{selectedUsers.length > 0 ? (
						selectedUsers.map(({ id, firstName, lastName }) => (
							<div className="selected-user" key={id}>
								{firstName} {lastName}
								<CloseIcon
									onClick={e => {
										e.stopPropagation();
										if (!disabled) {
											setSelectedUsers(selectedUsers.filter(user => user.id !== id));
										}
									}}
								/>
							</div>
						))
					) : (
						<div className="no-values no-values--absolute">
							{/*{t('standard.singleStandard.stageUpdateModal.additional.noValues')}*/}
						</div>
					)}
					<input
						type="text"
						className="search-input"
						value={searchQuery}
						disabled={disabled}
						placeholder={generatePlaceholder(fieldTitle)}
						onClick={e => e.stopPropagation()}
						onChange={e => {
							setSearchQuery(e.currentTarget.value);
							if (!opened && !disabled) {
								setOpened(true);
							}
						}}
					/>
					<ArrowIcon className="arrow" />
				</div>
				{opened && (
					<div className="popup">
						<div className="popup-body">
							{isSearchActive ? (
								isSearchUsersLoading ? (
									<Flex $justify="center" className="loading">
										<Loading width={25} height={25} />
									</Flex>
								) : !!searchedUsers && searchedUsers.data.length ? (
									searchedUsers.data.map(user => (
										<UserRow
											key={user.id}
											user={user}
											selectedUsers={selectedUsers}
											onUserSelectedChange={handleUserSelectedChange}
										/>
									))
								) : (
									<div className="empty">
										{t('standard.singleStandard.stageUpdateModal.additional.noUsers')}
									</div>
								)
							) : isDomainsLoading ? (
								<Flex $justify="center" className="loading">
									<Loading width={25} height={25} />
								</Flex>
							) : (
								<>
									<OtherUsers
										selectedUsers={selectedUsers}
										onUserSelectedChange={handleUserSelectedChange}
										onAllUsersSelectedChange={handleAllUsersInDomainSelectedChange}
									/>

									{!!domains &&
										domains.map(({ code, name, workingGroups }) => (
											<Domain
												key={code}
												code={code}
												name={name}
												workingGroups={workingGroups}
												selectedUsers={selectedUsers}
												onUserSelectedChange={handleUserSelectedChange}
												onAllUsersSelectedChange={handleAllUsersInDomainSelectedChange}
											/>
										))}
									<NotAllocatedUsers
										selectedUsers={selectedUsers}
										onUserSelectedChange={handleUserSelectedChange}
										onAllUsersSelectedChange={handleAllUsersInDomainSelectedChange}
									/>
								</>
							)}
						</div>
					</div>
				)}
			</div>
			{error ? (
				<StyledFieldErrorMessage>{error}</StyledFieldErrorMessage>
			) : null}
		</StyledUserSelect>
	);
};
